'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type MigrationType = 'schema-change' | 'data-migration' | 'platform-migration' | 'version-upgrade';
type DatabaseType = 'postgresql' | 'mysql' | 'mongodb' | 'dynamodb' | 'redis' | 'other';
type MigrationStrategy = 'blue-green' | 'rolling' | 'canary' | 'big-bang' | 'phased';
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

interface RiskFactor {
  factor: string;
  score: number; // 1-5
  weight: number; // 0-1
  mitigation: string;
}

const MIGRATION_TYPE_INFO: Record<MigrationType, { name: string; description: string; typicalDowntime: string }> = {
  'schema-change': {
    name: 'Schema Change',
    description: 'Alter tables, add/remove columns, modify indexes',
    typicalDowntime: '5-30 minutes',
  },
  'data-migration': {
    name: 'Data Migration',
    description: 'Move data between tables, databases, or platforms',
    typicalDowntime: '1-8 hours',
  },
  'platform-migration': {
    name: 'Platform Migration',
    description: 'Move to different database platform (e.g., MySQL → PostgreSQL)',
    typicalDowntime: '4-24 hours',
  },
  'version-upgrade': {
    name: 'Version Upgrade',
    description: 'Upgrade database to newer version',
    typicalDowntime: '30 minutes - 4 hours',
  },
};

const STRATEGY_INFO: Record<MigrationStrategy, { name: string; description: string; pros: string[]; cons: string[]; downtime: string }> = {
  'blue-green': {
    name: 'Blue-Green Deployment',
    description: 'Run two identical environments, switch traffic instantly',
    pros: ['Zero downtime', 'Instant rollback', 'Full testing before switch'],
    cons: ['2x infrastructure cost', 'Complex data sync', 'Requires load balancer'],
    downtime: '0 minutes',
  },
  'rolling': {
    name: 'Rolling Migration',
    description: 'Gradually migrate servers/instances one at a time',
    pros: ['Reduced risk', 'Phased approach', 'Can pause mid-migration'],
    cons: ['Longer migration window', 'Version compatibility needed', 'Complex coordination'],
    downtime: '0-30 minutes',
  },
  'canary': {
    name: 'Canary Release',
    description: 'Deploy to small subset of users first, then expand',
    pros: ['Early problem detection', 'Limited blast radius', 'Real user testing'],
    cons: ['Requires traffic splitting', 'Complex monitoring', 'Dual-write challenges'],
    downtime: '0-15 minutes',
  },
  'big-bang': {
    name: 'Big Bang',
    description: 'Switch entire system at once during maintenance window',
    pros: ['Simple to execute', 'No dual-write logic', 'Lower complexity'],
    cons: ['High risk', 'Full downtime', 'Difficult rollback'],
    downtime: '2-8 hours',
  },
  'phased': {
    name: 'Phased Migration',
    description: 'Migrate features/modules incrementally over time',
    pros: ['Lowest risk', 'Manageable chunks', 'Easy rollback'],
    cons: ['Longest timeline', 'Complex coordination', 'Tech debt during transition'],
    downtime: '0-30 minutes per phase',
  },
};

export default function DatabaseMigrationRiskAssessorPage() {
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'database-migration-risk-assessor',
    toolCategory: 'analyzer',
  });

  // Migration Details
  const [migrationType, setMigrationType] = useState<MigrationType>('schema-change');
  const [databaseType, setDatabaseType] = useState<DatabaseType>('postgresql');
  const [databaseSizeGB, setDatabaseSizeGB] = useState<number>(100);
  const [recordCount, setRecordCount] = useState<number>(10000000);
  const [dailyActiveUsers, setDailyActiveUsers] = useState<number>(100000);

  // Strategy
  const [selectedStrategy, setSelectedStrategy] = useState<MigrationStrategy>('blue-green');

  // Risk Factors
  const [riskFactors, setRiskFactors] = useState<RiskFactor[]>([
    {
      factor: 'Data Volume',
      score: 3,
      weight: 0.2,
      mitigation: 'Test with production-sized datasets, use parallel processing',
    },
    {
      factor: 'Schema Complexity',
      score: 3,
      weight: 0.15,
      mitigation: 'Document all foreign keys, indexes, and constraints',
    },
    {
      factor: 'Downtime Tolerance',
      score: 4,
      weight: 0.25,
      mitigation: 'Use blue-green deployment or rolling migration',
    },
    {
      factor: 'Rollback Difficulty',
      score: 3,
      weight: 0.2,
      mitigation: 'Create full backup, test rollback procedure',
    },
    {
      factor: 'Team Experience',
      score: 3,
      weight: 0.1,
      mitigation: 'Run dry runs, involve senior DBAs, document procedures',
    },
    {
      factor: 'Production Testing',
      score: 2,
      weight: 0.1,
      mitigation: 'Test on staging with production data clone',
    },
  ]);

  // Testing checkboxes
  const [hasBackup, setHasBackup] = useState(false);
  const [hasRollbackPlan, setHasRollbackPlan] = useState(false);
  const [hasLoadTesting, setHasLoadTesting] = useState(false);
  const [hasStagingTest, setHasStagingTest] = useState(false);
  const [hasMonitoring, setHasMonitoring] = useState(false);

  // Calculations
  const calculations = useMemo(() => {
    // Calculate overall risk score (1-5)
    const totalScore = riskFactors.reduce((sum, rf) => sum + (rf.score * rf.weight), 0);
    const riskLevel: RiskLevel =
      totalScore >= 4 ? 'critical' :
      totalScore >= 3 ? 'high' :
      totalScore >= 2 ? 'medium' : 'low';

    // Estimate downtime based on database size and migration type
    const baseMigrationTimeHours =
      migrationType === 'schema-change' ? 0.5 :
      migrationType === 'data-migration' ? 2 :
      migrationType === 'platform-migration' ? 12 :
      migrationType === 'version-upgrade' ? 2 : 1;

    const sizeMultiplier = Math.log10(databaseSizeGB + 1) / 2;
    const estimatedMigrationHours = baseMigrationTimeHours * sizeMultiplier;

    // Strategy affects downtime
    const strategyMultiplier =
      selectedStrategy === 'blue-green' ? 0 :
      selectedStrategy === 'rolling' ? 0.1 :
      selectedStrategy === 'canary' ? 0.05 :
      selectedStrategy === 'big-bang' ? 1.0 :
      selectedStrategy === 'phased' ? 0.2 : 1.0;

    const estimatedDowntimeHours = estimatedMigrationHours * strategyMultiplier;
    const estimatedDowntimeMinutes = Math.round(estimatedDowntimeHours * 60);

    // Calculate affected users during downtime
    const usersPerHour = dailyActiveUsers / 24;
    const affectedUsers = Math.round(usersPerHour * estimatedDowntimeHours);

    // Readiness score based on checkboxes
    const readinessChecks = [
      hasBackup,
      hasRollbackPlan,
      hasLoadTesting,
      hasStagingTest,
      hasMonitoring,
    ];
    const readinessScore = (readinessChecks.filter(Boolean).length / readinessChecks.length) * 100;

    // Rollback time estimate (usually faster than migration)
    const rollbackTimeHours = estimatedMigrationHours * 0.3;
    const rollbackTimeMinutes = Math.round(rollbackTimeHours * 60);

    return {
      totalScore,
      riskLevel,
      estimatedMigrationHours,
      estimatedDowntimeHours,
      estimatedDowntimeMinutes,
      affectedUsers,
      readinessScore,
      rollbackTimeHours,
      rollbackTimeMinutes,
      isReady: readinessScore >= 80,
    };
  }, [migrationType, databaseSizeGB, selectedStrategy, riskFactors, dailyActiveUsers, hasBackup, hasRollbackPlan, hasLoadTesting, hasStagingTest, hasMonitoring]);

  // Update risk factor
  const updateRiskFactor = (index: number, field: keyof RiskFactor, value: number | string) => {
    const updated = [...riskFactors];
    updated[index] = { ...updated[index], [field]: value };
    setRiskFactors(updated);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const gaps = riskFactors
      .filter(rf => rf.score >= 4)
      .map(rf => ({
        id: rf.factor.toLowerCase().replace(/\s+/g, '-'),
        title: rf.factor,
        severity: rf.score >= 5 ? 'critical' as const : 'high' as const,
        description: `Risk score: ${rf.score}/5`,
        recommendation: rf.mitigation,
      }));

    const recommendations = [
      ...(calculations.riskLevel === 'critical' || calculations.riskLevel === 'high' ? [{
        id: 'reduce-risk',
        title: 'Reduce Migration Risk',
        priority: 'high' as const,
        description: `Current risk level is ${calculations.riskLevel}. Address high-scoring risk factors before proceeding.`,
        effort: 'High',
      }] : []),
      ...(!calculations.isReady ? [{
        id: 'complete-checklist',
        title: 'Complete Readiness Checklist',
        priority: 'high' as const,
        description: `Readiness score is ${calculations.readinessScore.toFixed(0)}%. Complete all checklist items.`,
        effort: 'Medium',
      }] : []),
      {
        id: 'use-strategy',
        title: `Use ${STRATEGY_INFO[selectedStrategy].name} Strategy`,
        priority: 'medium' as const,
        description: STRATEGY_INFO[selectedStrategy].description,
        effort: selectedStrategy === 'blue-green' ? 'High' : selectedStrategy === 'big-bang' ? 'Low' : 'Medium',
      },
    ];

    const assessment = await saveResult({
      name: `Database Migration Risk Assessment - ${new Date().toLocaleDateString()}`,
      score_overall: Math.round((5 - calculations.totalScore) / 5 * 100), // Convert risk to readiness score
      scores: {
        riskScore: calculations.totalScore,
        readinessScore: calculations.readinessScore,
        estimatedDowntimeMinutes: calculations.estimatedDowntimeMinutes,
        affectedUsers: calculations.affectedUsers,
      },
      answers: {
        migrationType,
        databaseType,
        databaseSizeGB,
        recordCount,
        dailyActiveUsers,
        selectedStrategy,
        hasBackup,
        hasRollbackPlan,
        hasLoadTesting,
        hasStagingTest,
        hasMonitoring,
      },
      gaps,
      recommendations,
      metadata: {
        riskLevel: calculations.riskLevel,
        estimatedMigrationHours: calculations.estimatedMigrationHours,
        rollbackTimeMinutes: calculations.rollbackTimeMinutes,
        isReady: calculations.isReady,
        strategyName: STRATEGY_INFO[selectedStrategy].name,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => {
      const XLSX = (window as any).XLSX;
      const wb = XLSX.utils.book_new();

      // Sheet 1: Risk Assessment Summary
      const summaryData = [
        ['Database Migration Risk Assessment', ''],
        ['', ''],
        ['Migration Details', ''],
        ['Type', MIGRATION_TYPE_INFO[migrationType].name],
        ['Database', databaseType.toUpperCase()],
        ['Size (GB)', databaseSizeGB],
        ['Record Count', recordCount.toLocaleString()],
        ['Daily Active Users', dailyActiveUsers.toLocaleString()],
        ['', ''],
        ['Strategy', STRATEGY_INFO[selectedStrategy].name],
        ['', ''],
        ['Risk Assessment', ''],
        ['Overall Risk Score', calculations.totalScore.toFixed(2) + ' / 5'],
        ['Risk Level', calculations.riskLevel.toUpperCase()],
        ['', ''],
        ['Estimates', ''],
        ['Migration Time', calculations.estimatedMigrationHours.toFixed(1) + ' hours'],
        ['Estimated Downtime', calculations.estimatedDowntimeMinutes + ' minutes'],
        ['Rollback Time', calculations.rollbackTimeMinutes + ' minutes'],
        ['Affected Users', calculations.affectedUsers.toLocaleString()],
        ['', ''],
        ['Readiness', ''],
        ['Readiness Score', calculations.readinessScore.toFixed(0) + '%'],
        ['Ready to Migrate?', calculations.isReady ? 'YES' : 'NO'],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 25 }, { wch: 30 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Sheet 2: Risk Factors
      const riskData = riskFactors.map((rf) => ({
        'Risk Factor': rf.factor,
        'Score (1-5)': rf.score,
        'Weight': (rf.weight * 100).toFixed(0) + '%',
        'Weighted Score': (rf.score * rf.weight).toFixed(2),
        'Mitigation': rf.mitigation,
      }));

      const ws2 = XLSX.utils.json_to_sheet(riskData);
      ws2['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 50 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Risk Factors');

      // Sheet 3: Migration Checklist
      const checklistData = [
        { 'Phase': 'Pre-Migration', 'Task': 'Create full database backup', 'Status': hasBackup ? 'Done' : 'Pending', 'Owner': '' },
        { 'Phase': 'Pre-Migration', 'Task': 'Document rollback procedure', 'Status': hasRollbackPlan ? 'Done' : 'Pending', 'Owner': '' },
        { 'Phase': 'Pre-Migration', 'Task': 'Test on staging environment', 'Status': hasStagingTest ? 'Done' : 'Pending', 'Owner': '' },
        { 'Phase': 'Pre-Migration', 'Task': 'Run load testing', 'Status': hasLoadTesting ? 'Done' : 'Pending', 'Owner': '' },
        { 'Phase': 'Pre-Migration', 'Task': 'Set up monitoring and alerts', 'Status': hasMonitoring ? 'Done' : 'Pending', 'Owner': '' },
        { 'Phase': 'Pre-Migration', 'Task': 'Communicate to stakeholders', 'Status': '', 'Owner': '' },
        { 'Phase': 'Pre-Migration', 'Task': 'Schedule maintenance window', 'Status': '', 'Owner': '' },
        { 'Phase': 'Migration', 'Task': 'Enable maintenance mode', 'Status': '', 'Owner': '' },
        { 'Phase': 'Migration', 'Task': 'Execute migration script', 'Status': '', 'Owner': '' },
        { 'Phase': 'Migration', 'Task': 'Verify data integrity', 'Status': '', 'Owner': '' },
        { 'Phase': 'Migration', 'Task': 'Run smoke tests', 'Status': '', 'Owner': '' },
        { 'Phase': 'Post-Migration', 'Task': 'Monitor error rates', 'Status': '', 'Owner': '' },
        { 'Phase': 'Post-Migration', 'Task': 'Monitor performance metrics', 'Status': '', 'Owner': '' },
        { 'Phase': 'Post-Migration', 'Task': 'Verify backup completed', 'Status': '', 'Owner': '' },
        { 'Phase': 'Post-Migration', 'Task': 'Update documentation', 'Status': '', 'Owner': '' },
        { 'Phase': 'Post-Migration', 'Task': 'Conduct post-mortem', 'Status': '', 'Owner': '' },
      ];

      const ws3 = XLSX.utils.json_to_sheet(checklistData);
      ws3['!cols'] = [{ wch: 15 }, { wch: 35 }, { wch: 12 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Migration Checklist');

      // Sheet 4: Strategy Comparison
      const strategyData = Object.entries(STRATEGY_INFO).map(([key, info]) => ({
        'Strategy': info.name,
        'Downtime': info.downtime,
        'Complexity': info.pros.length > 3 ? 'Medium' : 'High',
        'Cost': key === 'blue-green' ? 'High' : key === 'big-bang' ? 'Low' : 'Medium',
        'Risk': key === 'big-bang' ? 'High' : key === 'phased' ? 'Low' : 'Medium',
        'Selected': key === selectedStrategy ? 'Yes' : 'No',
      }));

      const ws4 = XLSX.utils.json_to_sheet(strategyData);
      ws4['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, ws4, 'Strategy Comparison');

      (wb.Workbook as any) = { CalcPr: { fullCalcOnLoad: true } };
      XLSX.writeFile(wb, 'database-migration-risk-assessment.xlsx');
    };
    document.head.appendChild(script);
  };

  const getRiskColor = (level: RiskLevel) => {
    switch (level) {
      case 'low': return 'text-green-500';
      case 'medium': return 'text-yellow-500';
      case 'high': return 'text-accent';
      case 'critical': return 'text-red-500';
    }
  };

  const getRiskBgColor = (level: RiskLevel) => {
    switch (level) {
      case 'low': return 'bg-green-500/10 border-green-500/30';
      case 'medium': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'high': return 'bg-accent-muted border-border';
      case 'critical': return 'bg-red-500/10 border-red-500/30';
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/tools" className="text-accent hover:text-accent mb-4 inline-block">
            ← Back to Tools
          </Link>
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-foreground">
            Database Migration Risk Assessor
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
            Assess migration risks, estimate downtime, compare strategies, and generate migration checklists.
            Plan safer database migrations with data-driven insights.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Configuration */}
          <div className="space-y-6">
            {/* Migration Details */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Migration Details
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Migration Type
                  </label>
                  <select
                    value={migrationType}
                    onChange={(e) => setMigrationType(e.target.value as MigrationType)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  >
                    {Object.entries(MIGRATION_TYPE_INFO).map(([key, info]) => (
                      <option key={key} value={key}>{info.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                    {MIGRATION_TYPE_INFO[migrationType].description}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Database Type
                  </label>
                  <select
                    value={databaseType}
                    onChange={(e) => setDatabaseType(e.target.value as DatabaseType)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  >
                    <option value="postgresql">PostgreSQL</option>
                    <option value="mysql">MySQL</option>
                    <option value="mongodb">MongoDB</option>
                    <option value="dynamodb">DynamoDB</option>
                    <option value="redis">Redis</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Database Size (GB)
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={databaseSizeGB}
                    onChange={(e) => setDatabaseSizeGB(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Record Count
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={recordCount}
                    onChange={(e) => setRecordCount(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-light-text dark:text-foreground">
                    Daily Active Users
                  </label>
                  <input
                    type="number" inputMode="numeric"
                    value={dailyActiveUsers}
                    onChange={(e) => setDailyActiveUsers(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground"
                  />
                </div>
              </div>
            </div>

            {/* Risk Factors */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Risk Factors
              </h2>

              <div className="space-y-4">
                {riskFactors.map((rf, index) => (
                  <div key={index} className="border border-gray-300 dark:border-border rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-light-text dark:text-foreground">{rf.factor}</span>
                      <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary">
                        Weight: {(rf.weight * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="mb-2">
                      <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                        Risk Score: {rf.score} / 5
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="5"
                        value={rf.score}
                        onChange={(e) => updateRiskFactor(index, 'score', parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                    <p className="text-xs text-light-text-secondary dark:text-foreground-secondary">
                      <span className="font-semibold">Mitigation:</span> {rf.mitigation}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Readiness Checklist */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Readiness Checklist
              </h2>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasBackup}
                    onChange={(e) => setHasBackup(e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-light-text dark:text-foreground">Full database backup created</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasRollbackPlan}
                    onChange={(e) => setHasRollbackPlan(e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-light-text dark:text-foreground">Rollback procedure documented and tested</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasStagingTest}
                    onChange={(e) => setHasStagingTest(e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-light-text dark:text-foreground">Tested on staging with production data</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasLoadTesting}
                    onChange={(e) => setHasLoadTesting(e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-light-text dark:text-foreground">Load testing completed</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasMonitoring}
                    onChange={(e) => setHasMonitoring(e.target.checked)}
                    className="w-5 h-5"
                  />
                  <span className="text-light-text dark:text-foreground">Monitoring and alerts configured</span>
                </label>

                <div className="pt-4 border-t border-gray-300 dark:border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-light-text-secondary dark:text-foreground-secondary">Readiness Score</span>
                    <span className={`text-xl font-bold ${
                      calculations.readinessScore >= 80 ? 'text-green-500' :
                      calculations.readinessScore >= 60 ? 'text-yellow-500' : 'text-red-500'
                    }`}>
                      {calculations.readinessScore.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Results */}
          <div className="space-y-6">
            {/* Risk Assessment */}
            <div className={`border rounded-lg p-6 ${getRiskBgColor(calculations.riskLevel)}`}>
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Risk Assessment
              </h2>

              <div className="text-center mb-6">
                <div className={`text-5xl font-bold mb-2 ${getRiskColor(calculations.riskLevel)}`}>
                  {calculations.riskLevel.toUpperCase()}
                </div>
                <div className="text-light-text-secondary dark:text-foreground-secondary">
                  Risk Score: {calculations.totalScore.toFixed(2)} / 5.0
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Estimated Downtime
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.estimatedDowntimeMinutes}m
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Affected Users
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.affectedUsers.toLocaleString()}
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Migration Time
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.estimatedMigrationHours.toFixed(1)}h
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Rollback Time
                  </div>
                  <div className="text-2xl font-bold text-light-text dark:text-foreground">
                    {calculations.rollbackTimeMinutes}m
                  </div>
                </div>
              </div>

              {!calculations.isReady && (
                <div className="mt-4 bg-red-500/20 border border-red-500/40 rounded-lg p-3">
                  <p className="text-sm text-red-500 font-semibold">
                    ⚠️ Not Ready: Complete readiness checklist before migrating (currently {calculations.readinessScore.toFixed(0)}%)
                  </p>
                </div>
              )}
            </div>

            {/* Migration Strategy */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Migration Strategy
              </h2>

              <div className="space-y-3">
                {Object.entries(STRATEGY_INFO).map(([key, info]) => (
                  <div
                    key={key}
                    onClick={() => setSelectedStrategy(key as MigrationStrategy)}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedStrategy === key
                        ? 'border-accent bg-accent-muted'
                        : 'border-gray-300 dark:border-border hover:border-border-muted0'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <input
                        type="radio"
                        checked={selectedStrategy === key}
                        onChange={() => setSelectedStrategy(key as MigrationStrategy)}
                        className="w-4 h-4"
                      />
                      <div>
                        <div className="font-semibold text-light-text dark:text-foreground">{info.name}</div>
                        <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary">
                          Downtime: {info.downtime}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-light-text-secondary dark:text-foreground-secondary ml-7">
                      {info.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Strategy Details */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Selected Strategy Details
              </h2>

              <div className="space-y-4">
                <div>
                  <div className="font-semibold text-light-text dark:text-foreground mb-2">
                    {STRATEGY_INFO[selectedStrategy].name}
                  </div>
                  <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                    {STRATEGY_INFO[selectedStrategy].description}
                  </p>
                </div>

                <div>
                  <div className="text-sm font-semibold text-green-500 mb-2">Pros:</div>
                  <ul className="space-y-1">
                    {STRATEGY_INFO[selectedStrategy].pros.map((pro, idx) => (
                      <li key={idx} className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                        ✓ {pro}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <div className="text-sm font-semibold text-red-500 mb-2">Cons:</div>
                  <ul className="space-y-1">
                    {STRATEGY_INFO[selectedStrategy].cons.map((con, idx) => (
                      <li key={idx} className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                        ✗ {con}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-3 border-t border-gray-300 dark:border-border">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-light-text-secondary dark:text-foreground-tertiary">Expected Downtime</div>
                      <div className="font-semibold text-light-text dark:text-foreground">
                        {STRATEGY_INFO[selectedStrategy].downtime}
                      </div>
                    </div>
                    <div>
                      <div className="text-light-text-secondary dark:text-foreground-tertiary">Typical Duration</div>
                      <div className="font-semibold text-light-text dark:text-foreground">
                        {MIGRATION_TYPE_INFO[migrationType].typicalDowntime}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Export & Save */}
            <div className="space-y-3">
              <button
                onClick={exportToExcel}
                className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
              >
                Export Migration Plan to Excel
              </button>
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm text-center">
                  {saveError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pro Features */}
        <div className="mt-8 bg-gradient-to-r from-accent-muted to-accent/5 border-l-4 border-accent p-6 rounded-lg">
          <h3 className="text-xl font-bold text-accent mb-3">⭐ Unlock Pro Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Pro ($29/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Database-specific assessments (10+ databases)</li>
                <li>✓ Rollback strategy templates</li>
                <li>✓ Migration pattern comparison</li>
                <li>✓ Excel export with timeline</li>
                <li>✓ Risk mitigation strategies</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Team ($99/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Everything in Pro</li>
                <li>✓ Database-specific migration plans</li>
                <li>✓ Auto-generated migration scripts</li>
                <li>✓ Compliance impact assessment</li>
                <li>✓ Historical migration knowledge base</li>
              </ul>
            </div>
          </div>
          <Link
            href="/pricing"
            className="inline-block px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
          >
            Upgrade to Pro →
          </Link>
        </div>
      </div>
    </div>
  );
}
